import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'phosphor-react';
import usePresence from '../../use/usePresence';

interface LightboxProps {
  aberto: boolean;
  onClose: () => void;
  /** A mídia em si — imagem, vídeo, o que for. Quem chama decide. */
  children: React.ReactNode;
}

/**
 * Tela cheia preta com a mídia no meio. Existia em três cópias (o detalhe da foto
 * de pele, o registro de progresso e a mídia do relato de bug), e só uma delas
 * tinha animação de saída.
 *
 * Vai por portal para escapar de qualquer `overflow: hidden` — e de qualquer
 * ancestral com `filter`/`transform`, que criaria containing block e faria este
 * `fixed` se posicionar pelo pai em vez da viewport.
 */
export default function Lightbox({ aberto, onClose, children }: LightboxProps) {
  const { montado, estado } = usePresence(aberto, 180);

  useEffect(() => {
    if (!aberto) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [aberto, onClose]);

  if (!montado) return null;

  return ReactDOM.createPortal(
    <div
      data-state={estado}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out data-[state=closed]:pointer-events-none"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors active:scale-[0.97]"
        aria-label="Fechar"
      >
        <X className="w-6 h-6" />
      </button>
      <div
        data-state={estado}
        className="max-w-5xl max-h-screen w-full px-4 flex items-center justify-center data-[state=open]:animate-scale-in data-[state=closed]:animate-scale-out"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
